import React, { useState } from 'react';
import { supabase } from '../supabase';
import { C, S } from '../styles';
import { sendEmail } from '../utils/emailService';
import { mcRegistrationNotification } from '../utils/emailTemplates';

const PROLINE_ADMIN_EMAIL = 'simon@prolineinternals.co.uk';

const BRAND_STYLE = {
  background: `linear-gradient(135deg, ${C.header} 0%, ${C.sidebar} 100%)`,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const CARD_STYLE = {
  background: C.card,
  borderRadius: '12px',
  padding: '40px',
  width: '100%',
  maxWidth: '440px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

function InputField({ label, type = 'text', value, onChange, placeholder, required }) {
  return (
    <div style={S.formGroup}>
      <label style={S.label}>{label}{required && <span style={{ color: C.danger }}> *</span>}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        style={S.input}
      />
    </div>
  );
}

export function LoginPage({ navigate }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
    } catch (err) {
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={BRAND_STYLE}>
      <div style={CARD_STYLE}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/Proline Logo HQ.png" alt="Proline Internals" style={{ height: '120px', width: 'auto', marginBottom: '16px' }} />
        </div>
        <h2 style={{ textAlign: 'center', fontSize: '18px', fontWeight: '600', color: C.text, marginBottom: '24px', marginTop: 0 }}>
          Sign In
        </h2>
        {error && <div style={S.alertError}>{error}</div>}
        <form onSubmit={handleLogin}>
          <InputField label="Email Address" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required />
          <InputField label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" required />
          <button
            type="submit"
            disabled={loading}
            style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, width: '100%', justifyContent: 'center', marginTop: '8px', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: C.textMuted }}>
          Don't have an account?{' '}
          <button
            onClick={() => navigate('register', {})}
            style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: '14px', padding: 0, fontWeight: '500' }}
          >
            Request Access
          </button>
        </div>
      </div>
    </div>
  );
}

export function RegisterPage({ navigate }) {
  const [role, setRole] = useState('proline_sm');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [siteName, setSiteName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) { setError('Full name is required.'); return; }
    if (!siteName.trim()) { setError('Site name is required.'); return; }
    if (role === 'mc_qs' && !company.trim()) { setError('Company name is required.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    setLoading(true);
    try {
      const metadata = {
        full_name: fullName,
        role,
        phone,
        site_name: siteName,
        company: role === 'mc_qs' ? company : '',
      };

      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { data: metadata },
      });

      if (signUpError) throw signUpError;

      if (data?.user) {
        await supabase.from('profiles').upsert({
          id: data.user.id,
          email,
          full_name: fullName,
          role,
          phone,
          site_name: siteName,
          company: role === 'mc_qs' ? company : '',
          approved: false,
        }, { onConflict: 'id' });

        if (role === 'mc_qs') {
          await supabase.from('mc_registration_requests').insert({
            user_id: data.user.id,
            full_name: fullName,
            email,
            company,
            site_name_given: siteName,
          });
          try {
            const { subject, htmlBody } = mcRegistrationNotification(fullName, email, company, siteName);
            await sendEmail({ to: PROLINE_ADMIN_EMAIL, subject, htmlBody });
          } catch (emailErr) {
            console.error('MC registration notification email failed:', emailErr);
          }
        }
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    const isMcQsSuccess = role === 'mc_qs';
    return (
      <div style={BRAND_STYLE}>
        <div style={{ ...CARD_STYLE, maxWidth: '480px' }}>
          <div style={{ textAlign: 'center', padding: '8px 0 24px' }}>
            <img src="/Proline Logo HQ.png" alt="Proline Internals" style={{ height: '120px', width: 'auto', marginBottom: '16px' }} />
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: '32px',
            }}>
              ✓
            </div>
            <h2 style={{ color: '#065f46', fontSize: '22px', fontWeight: '700', margin: '0 0 12px' }}>Request Received</h2>
            {isMcQsSuccess ? (
              <p style={{ color: C.textMuted, fontSize: '14px', lineHeight: '1.7', margin: '0 0 28px' }}>
                Your MC portal access request has been submitted to Proline Internals. A member of the Proline team will verify your details, assign you to the correct project, and send you an email confirmation once approved. This usually happens within one business day.
              </p>
            ) : (
              <p style={{ color: C.textMuted, fontSize: '14px', lineHeight: '1.7', margin: '0 0 28px' }}>
                Your access request has been submitted. A member of the Proline team will review it and you will receive an email confirmation once access has been granted. This usually happens within 24 hours.
              </p>
            )}
            <button
              onClick={() => navigate('login', {})}
              style={{ ...S.btn, ...S.btnPrimary }}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  const ROLE_OPTIONS = [
    { value: 'proline_sm', label: 'Proline Site Manager', sub: 'Proline operative on site' },
    { value: 'mc_qs', label: 'Main Contractor Project Team', sub: 'Main Contractor commercial or site team' },
  ];

  return (
    <div style={BRAND_STYLE}>
      <div style={{ ...CARD_STYLE, maxWidth: '520px' }}>
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <img src="/Proline Logo HQ.png" alt="Proline Internals" style={{ height: '120px', width: 'auto', marginBottom: '16px' }} />
        </div>
        <h2 style={{ textAlign: 'center', fontSize: '18px', fontWeight: '600', color: C.text, marginBottom: '24px', marginTop: 0 }}>
          Request Access
        </h2>
        {error && <div style={S.alertError}>{error}</div>}
        <form onSubmit={handleRegister}>
          {/* Role selector */}
          <div style={{ ...S.formGroup, marginBottom: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              {ROLE_OPTIONS.map(opt => (
                <div
                  key={opt.value}
                  onClick={() => setRole(opt.value)}
                  style={{
                    padding: '14px',
                    border: `2px solid ${role === opt.value ? C.accent : C.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    background: role === opt.value ? '#f0f0f0' : '#fff',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ fontWeight: '600', fontSize: '14px', color: role === opt.value ? C.accent : C.text }}>
                    {opt.label}
                  </div>
                  <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '4px' }}>
                    {opt.sub}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <InputField label="Full Name" value={fullName} onChange={setFullName} placeholder="John Smith" required />
          <InputField label="Email Address" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required />
          {role === 'mc_qs' && (
            <InputField label="Main Contractor Company" value={company} onChange={setCompany} placeholder="e.g. Caddick Construction" required />
          )}
          <InputField label="Site Name" value={siteName} onChange={setSiteName} placeholder="Which project/site are you working on?" required />
          <InputField label="Phone" value={phone} onChange={setPhone} placeholder="+44 7700 900000" />
          <InputField label="Password" type="password" value={password} onChange={setPassword} placeholder="Minimum 6 characters" required />

          <button
            type="submit"
            disabled={loading}
            style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, width: '100%', justifyContent: 'center', marginTop: '8px', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Submitting...' : 'Request Access'}
          </button>
        </form>
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: C.textMuted }}>
          Already have an account?{' '}
          <button
            onClick={() => navigate('login', {})}
            style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: '14px', padding: 0, fontWeight: '500' }}
          >
            Sign In
          </button>
        </div>
      </div>
    </div>
  );
}
